import React, { useCallback, useState } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import { View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import { useStyle } from 'react-native-style-utilities';
import { isWebBrowser } from '../../../utils';
import type {
  LabelMeasurements,
  TextFieldFloatingLabelProps,
} from '../TextField.types';
import { BodyText } from '../../Text';
import {
  LABEL_ANIMATION_CONFIG,
  LABEL_BOTTOM_OFFSET,
  LABEL_IDLE_SCALE,
} from '../TextField.constants';
import { styles } from '../TextField.styles';

export const TextFieldFloatingLabel = (props: TextFieldFloatingLabelProps) => {
  const { label, labelOffset, isFieldActive, nativeID } = props;

  const [labelMeasurements, setLabelMeasurements] = useState<LabelMeasurements>(
    { width: 0, height: 0 },
  );

  // ─── HELPERS ────────────────────────────────────────────────────────

  const trackLabelMeasurements = useCallback((event: LayoutChangeEvent) => {
    setLabelMeasurements(event.nativeEvent.layout);
  }, []);

  const isLabelRendered = labelMeasurements.height !== 0;

  // ─── DYNAMIC STYLES ─────────────────────────────────────────────────

  const labelWrapperDynamicStyles = useStyle(() => {
    // hide initial flickering when label's height has not been calculated
    if (!isLabelRendered) return { opacity: 0 };

    return {
      transform: [{ translateY: -labelMeasurements.height / 2 }],
    };
  }, [isLabelRendered, labelMeasurements.height]);

  // ─── ANIMATED STYLES ────────────────────────────────────────────────

  const labelTranslationAnimatedStyles = useAnimatedStyle(() => {
    if (!isLabelRendered) return {};

    const { positionX, positionY } = calculatedFloatingLabelPosition(
      labelMeasurements,
      labelOffset,
    );

    const translateY = changeValueWithTiming(isFieldActive ? positionY : 0);
    const translateX = changeValueWithTiming(isFieldActive ? positionX : 0);

    return { transform: [{ translateY }, { translateX }] };
  }, [
    isLabelRendered,
    isFieldActive,
    labelMeasurements.width,
    labelMeasurements.height,
  ]);

  const labelScaleAnimatedStyles = useAnimatedStyle(() => {
    if (!isLabelRendered) return {};

    const scale = changeValueWithTiming(isFieldActive ? LABEL_IDLE_SCALE : 1);

    return { transform: [{ scale }] };
  }, [isLabelRendered, isFieldActive]);

  // ────────────────────────────────────────────────────────────────────

  if (!label) return null;

  return (
    <View
      style={[styles.labelWrapper, labelWrapperDynamicStyles]}
      onLayout={trackLabelMeasurements}
      pointerEvents="none"
    >
      <Animated.View style={labelTranslationAnimatedStyles}>
        <Animated.View
          style={labelScaleAnimatedStyles}
          pointerEvents={isFieldActive ? 'box-none' : 'none'}
        >
          <BodyText
            nativeID={nativeID}
            accessibilityRole={isWebBrowser() ? 'label' : undefined}
            size={3}
            style={styles.label}
            numberOfLines={1}
          >
            {label}
          </BodyText>
        </Animated.View>
      </Animated.View>
    </View>
  );
};

// ─── HELPERS ────────────────────────────────────────────────────────────────────

export const calculatedFloatingLabelPosition = (
  labelMeasurements: LabelMeasurements,
  labelOffset = 0,
) => {
  'worklet';

  const { width, height } = labelMeasurements;

  const leftPercentage = 1 - LABEL_IDLE_SCALE;
  const widthDifferenceAfterScale = width * leftPercentage;

  const positionX = -Math.ceil(widthDifferenceAfterScale / 2);
  const positionY = -(height + LABEL_BOTTOM_OFFSET + labelOffset);

  return { positionY, positionX };
};

export const changeValueWithTiming = (value: number) => {
  'worklet';

  return withTiming(value, LABEL_ANIMATION_CONFIG);
};
